import React, { useEffect, useState } from 'react'
import { api } from '../lib/api'

interface MCPServer {
  id: number
  name: string
  slug: string
  description: string
  category: string
  user_config_schema: {
    type: string
    properties: Record<string, any>
    required: string[]
  }
}

interface MCPConnectionDrawerProps {
  open: boolean
  onClose: () => void
  serverId: number | null
  connectionId?: number | null
  onSuccess: () => void
}

export default function MCPConnectionDrawer({ open, onClose, serverId, connectionId, onSuccess }: MCPConnectionDrawerProps) {
  const [server, setServer] = useState<MCPServer | null>(null)
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; tools_count?: number } | null>(null)
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [displayName, setDisplayName] = useState<string>('')

  useEffect(() => {
    if (open && serverId) {
      loadServer()
      if (connectionId) {
        loadConnection()
      }
    } else {
      // Reset state when closing
      setServer(null)
      setFormData({})
      setDisplayName('')
      setTestResult(null)
      setError(null)
    }
  }, [open, serverId, connectionId])

  const loadServer = async () => {
    try {
      setLoading(true)
      const res = await api.get('/mcp/servers')
      const servers = res.data.items || []
      const found = servers.find((s: any) => s.id === serverId)
      
      if (!found) {
        setError('Serveur MCP introuvable')
        return
      }
      
      setServer(found)
      
      // Initialize form with empty values for required fields (only if not editing)
      if (!connectionId && found.user_config_schema?.properties) {
        const initial: Record<string, string> = {}
        Object.keys(found.user_config_schema.properties).forEach(key => {
          initial[key] = found.user_config_schema.properties[key].default || ''
        })
        setFormData(initial)
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }

  const loadConnection = async () => {
    if (!connectionId) return
    
    try {
      setLoading(true)
      const res = await api.get(`/mcp/connections/${connectionId}`)
      const connection = res.data
      
      // Load existing config and display name
      if (connection.config) {
        setFormData(connection.config)
      }
      if (connection.display_name) {
        setDisplayName(connection.display_name)
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement de la connexion')
    } finally {
      setLoading(false)
    }
  }

  const handleTest = async () => {
    if (!server) return
    
    setTesting(true)
    setTestResult(null)
    setError(null)
    
    try {
      const res = await api.post('/mcp/connections/test', {
        mcp_server_id: server.id,
        config: formData
      })
      
      setTestResult({
        success: res.data.success,
        message: res.data.message,
        tools_count: res.data.tools_available?.length || 0
      })
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors du test')
      setTestResult({ success: false, message: 'Test échoué' })
    } finally {
      setTesting(false)
    }
  }

  const handleCreate = async () => {
    if (!server) return
    
    setCreating(true)
    setError(null)
    
    try {
      // Generate display name from token_type if available (WowBooking)
      const finalDisplayName = displayName.trim() || 
        (formData.token_type 
          ? `${server.name} - ${formData.token_type === 'public' ? 'Public' : 'Operator'}`
          : server.name)
      
      if (connectionId) {
        // Update existing connection
        await api.put(`/mcp/connections/${connectionId}`, {
          display_name: finalDisplayName,
          config: formData
        })
      } else {
        // Create new connection
        await api.post('/mcp/connections', {
          mcp_server_id: server.id,
          display_name: finalDisplayName,
          config: formData
        })
      }
      
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.detail || `Erreur lors de ${connectionId ? 'la mise à jour' : 'la création'}`)
    } finally {
      setCreating(false)
    }
  }

  if (!open) return null

  const schema = server?.user_config_schema
  const properties = schema?.properties || {}
  const required = schema?.required || []

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full md:w-1/2 bg-white dark:bg-zinc-900 shadow-2xl z-50 overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                {loading ? 'Chargement...' : connectionId ? `Modifier ${server?.name}` : `Connecter ${server?.name}`}
              </h2>
              {server && (
                <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
                  {server.description}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 text-2xl leading-none"
            >
              ×
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : server ? (
            <>
              {/* Error Alert */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-600 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Test Result */}
              {testResult && (
                <div className={`mb-4 p-4 rounded text-sm ${
                  testResult.success
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400'
                    : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400'
                }`}>
                  <p className="font-medium">{testResult.message}</p>
                  {testResult.success && testResult.tools_count !== undefined && (
                    <p className="mt-1 text-xs">
                      ✓ {testResult.tools_count} outils disponibles
                    </p>
                  )}
                </div>
              )}

              {/* Display Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                  Nom de la connexion
                  <span className="text-zinc-400 font-normal ml-2">(optionnel)</span>
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={server?.name || 'Ex: WowBooking - Public'}
                  className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-700 rounded bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                  Permet de distinguer plusieurs connexions au même serveur
                </p>
              </div>

              {/* Form */}
              <div className="space-y-4 mb-6">
                {Object.entries(properties).map(([key, prop]: [string, any]) => {
                  const isRequired = required.includes(key)
                  const isPassword = key.toLowerCase().includes('token') || 
                                   key.toLowerCase().includes('password') || 
                                   key.toLowerCase().includes('secret') ||
                                   key.toLowerCase().includes('key')
                  const hasEnum = prop.enum && Array.isArray(prop.enum)

                  return (
                    <div key={key}>
                      <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
                        {prop.title || prop.description || key}
                        {isRequired && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {prop.description && prop.title && (
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2">
                          {prop.description}
                        </p>
                      )}
                      
                      {hasEnum ? (
                        <select
                          value={formData[key] || prop.default || ''}
                          onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                          className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-700 rounded bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        >
                          <option value="">Sélectionner...</option>
                          {prop.enum.map((opt: string) => (
                            <option key={opt} value={opt}>
                              {opt === 'public' ? '🌐 Public (visiteur)' : 
                               opt === 'operator' ? '👤 Operator (staff)' : opt}
                            </option>
                          ))}
                        </select>
                      ) : prop.type === 'array' ? (
                        <textarea
                          value={formData[key] || ''}
                          onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                          placeholder={`Un élément par ligne`}
                          rows={3}
                          className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-700 rounded bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      ) : (
                        <input
                          type={isPassword ? 'password' : 'text'}
                          value={formData[key] || ''}
                          onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                          placeholder={prop.title || prop.description || key}
                          className="w-full px-3 py-2 border border-zinc-300 dark:border-zinc-700 rounded bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleTest}
                  disabled={testing || creating}
                  className="flex-1 px-4 py-3 bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 rounded-lg hover:bg-zinc-200 dark:hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  {testing ? '🔄 Test en cours...' : '🧪 Tester'}
                </button>
                
                <button
                  onClick={handleCreate}
                  disabled={!testResult?.success || creating}
                  className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  {creating ? (connectionId ? '⏳ Mise à jour...' : '⏳ Création...') : (connectionId ? '✓ Mettre à jour' : '✓ Créer')}
                </button>
              </div>

              <p className="mt-4 text-xs text-zinc-500 dark:text-zinc-400 text-center">
                💡 Testez d'abord la connexion avant de {connectionId ? 'la mettre à jour' : 'la créer'}
              </p>

              {/* Help sections */}
              {server.slug === 'github' && (
                <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2 text-sm">
                    📘 Comment obtenir un token GitHub ?
                  </h3>
                  <ol className="text-xs text-blue-800 dark:text-blue-200 space-y-1 list-decimal list-inside">
                    <li>Allez sur <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer" className="underline">github.com/settings/tokens</a></li>
                    <li>Cliquez sur "Generate new token (classic)"</li>
                    <li>Sélectionnez les permissions : <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">repo</code></li>
                    <li>Copiez le token généré</li>
                  </ol>
                </div>
              )}

              {server.slug === 'gitlab' && (
                <div className="mt-6 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                  <h3 className="font-semibold text-orange-900 dark:text-orange-100 mb-2 text-sm">
                    🦊 Comment obtenir un token GitLab ?
                  </h3>
                  <ol className="text-xs text-orange-800 dark:text-orange-200 space-y-1 list-decimal list-inside">
                    <li>Allez sur <a href="https://gitlab.com/-/user_settings/personal_access_tokens" target="_blank" rel="noopener noreferrer" className="underline">gitlab.com/...personal_access_tokens</a></li>
                    <li>Cliquez sur "Add new token"</li>
                    <li>Permissions : <code className="bg-orange-100 dark:bg-orange-900 px-1 rounded">api</code>, <code className="bg-orange-100 dark:bg-orange-900 px-1 rounded">read_repository</code>, <code className="bg-orange-100 dark:bg-orange-900 px-1 rounded">write_repository</code></li>
                    <li>Copiez le token généré</li>
                  </ol>
                </div>
              )}

              {server.slug === 'wowbooking' && (
                <div className="mt-6 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg p-4">
                  <h3 className="font-semibold text-cyan-900 dark:text-cyan-100 mb-2 text-sm">
                    🚤 Comment obtenir un token WowBooking ?
                  </h3>
                  <ol className="text-xs text-cyan-800 dark:text-cyan-200 space-y-1 list-decimal list-inside">
                    <li>Connectez-vous à votre compte WowBooking.ai</li>
                    <li>Allez dans Paramètres → API / Intégrations</li>
                    <li>Générez un token <strong>Public</strong> (visiteurs) ou <strong>Operator</strong> (staff)</li>
                    <li>Copiez le token généré</li>
                  </ol>
                  <div className="mt-3 pt-3 border-t border-cyan-200 dark:border-cyan-700">
                    <p className="text-xs text-cyan-700 dark:text-cyan-300">
                      <strong>Public :</strong> Accès lecture aux offres et disponibilités<br/>
                      <strong>Operator :</strong> Accès complet (réservations, clients, etc.)
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-red-600 dark:text-red-400">Serveur introuvable</p>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
